import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase'; // Adjust path
import styles from '../styles/EditClientPage.module.css'; // Create this CSS file (can reuse AddClientPage styles)

const EditClientPage = () => {
    const { clientId } = useParams();
    const navigate = useNavigate();

    // --- Form State ---
    const [name, setName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [address, setAddress] = useState('');
    const [notes, setNotes] = useState('');
    // --- Helper State ---
    const [loading, setLoading] = useState(true); // Loading initial data
    const [updating, setUpdating] = useState(false); // Saving changes
    const [error, setError] = useState('');
    const [clientExists, setClientExists] = useState(true);

    // Fetch Client Data
    useEffect(() => {
        const fetchClient = async () => {
            setLoading(true);
            setError('');
            setClientExists(true);
            try {
                const clientDocRef = doc(db, 'clients', clientId);
                const docSnap = await getDoc(clientDocRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setName(data.name || '');
                    setPhoneNumber(data.phoneNumber || '');
                    setAddress(data.address || '');
                    setNotes(data.notes || '');
                } else {
                    setError('Client not found.');
                    setClientExists(false);
                }
            } catch (err) {
                console.error("Error fetching client:", err);
                setError('Failed to load client data.');
                setClientExists(false);
            } finally {
                setLoading(false);
            }
        };
        fetchClient();
    }, [clientId]); // Re-fetch if clientId changes

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!name) {
            setError("Client name is required.");
            return;
        }
        setUpdating(true);

        try {
            const clientDocRef = doc(db, 'clients', clientId);
            await updateDoc(clientDocRef, {
                name,
                phoneNumber,
                address,
                notes,
                updatedAt: serverTimestamp(), // Update the timestamp
            });
            console.log("Client updated successfully!");
            navigate('/clients'); // Redirect back to list

        } catch (err) {
            console.error("Error updating client:", err);
            setError('Failed to update client. Please try again.');
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
         return <div className={styles.loadingIndicator}>Loading client data...</div>;
    }

    if (!clientExists) {
         return <div className={`${styles.pageContainer} ${styles.errorMessage}`}>{error || 'Client not found.'}</div>;
    }


    return (
        <div className={styles.pageContainer}>
            <div className={styles.formBox}>
                <h2 className={styles.formTitle}>Edit Client: {name}</h2> {/* Show name in title */}
                {error && <div className={styles.errorMessage}>{error}</div>}

                <form onSubmit={handleSubmit} className={styles.form}>
                     <div className={styles.inputGroup}>
                        <label htmlFor="name">Client Name</label>
                        <input type="text" id="name" value={name} onChange={(e) => setName(e.target.value)} required disabled={updating} />
                    </div>
                     <div className={styles.inputGroup}>
                        <label htmlFor="phoneNumber">Phone Number</label>
                        <input type="tel" id="phoneNumber" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} disabled={updating} />
                    </div>
                     <div className={styles.inputGroup}>
                        <label htmlFor="address">Address</label>
                        <textarea id="address" value={address} onChange={(e) => setAddress(e.target.value)} rows={3} disabled={updating} />
                    </div>
                    <div className={styles.inputGroup}>
                        <label htmlFor="notes">Notes</label>
                        <textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} disabled={updating} />
                    </div>

                    <div className={styles.buttonGroup}>
                        <button type="button" className={`${styles.formButton} ${styles.cancelButton}`} onClick={() => navigate('/clients')} disabled={updating}>
                            Cancel
                        </button>
                        <button type="submit" className={`${styles.formButton} ${styles.submitButton}`} disabled={updating}>
                            {updating ? <span className={styles.loadingSpinner}></span> : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditClientPage;